import { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router';
import {
    UIButton,
    UIInlineAlert,
    UISpacer,
    UISpinner,
    UISubNav,
    UIText,
    useUINav
} from '@/ui';
import { useCompanyContext } from '@/companies/providers';
import { useCompanyPermissions, useTeamPermissions } from '@/companies/hooks';
import { useFetchDevice } from '@/devices/queries';
import {
    DeviceActionsSection,
    DeviceAlertsSection,
    DeviceGeneralSection,
    DeviceGeofenceSection,
    DeviceIdLabel,
    DeviceStatus,
    DeviceWifiSection
} from '@/devices/components';
import { usePagesTranslation } from '../hooks';

export function DeviceIdSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { company } = useCompanyContext();
    const { canViewDevices, canEditDevices } = useCompanyPermissions();
    // `teamId` is only present on the team-scoped route
    // (teams/:teamId/devices/:deviceId), reached from a team's Devices tab.
    const { deviceId, teamId } = useParams();

    const { device, status, error, canRetry, refetch } = useFetchDevice(
        deviceId ?? ''
    );

    // The page is viewable by anyone the backend lets load the device (an
    // unauthorised GET surfaces as an error below). Editing is allowed for
    // company owners/admins, or a team admin of the device's team; everyone
    // else gets a read-only view.
    const { canEditDevices: canEditTeamDevices } = useTeamPermissions({
        teamId: device?.team?.teamId ?? ''
    });
    const canEdit = canEditDevices || canEditTeamDevices;

    const { setParts } = useUINav();
    useEffect(() => {
        if (teamId) {
            // Reached from a team's Devices tab — keep the breadcrumb within the
            // team, since a team admin can't access the company devices list.
            setParts([
                { text: t('shared.labels.settings') },
                {
                    text: t('shared.labels.teams'),
                    link: `/company/${company.id}/settings/teams`
                },
                {
                    text: device?.team?.name ?? t('shared.labels.team')
                },
                {
                    text: t('shared.labels.devices'),
                    link: `/company/${company.id}/settings/teams/${teamId}/devices`
                },
                {
                    text: device?.name ?? t('shared.labels.device')
                }
            ]);
            return;
        }
        setParts([
            { text: t('shared.labels.settings') },
            {
                text: t('shared.labels.devices'),
                // Only owners/admins can open the company devices list; don't
                // render a crumb that would redirect everyone else home.
                link: canViewDevices
                    ? `/company/${company.id}/settings/devices`
                    : undefined
            },
            {
                text: device?.name ?? t('shared.labels.device')
            }
        ]);
    }, [
        setParts,
        company.id,
        device?.name,
        device?.team?.name,
        teamId,
        canViewDevices,
        t
    ]);

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                padding: 32,
                gap: 24
            }}
        >
            <UISubNav
                title={device?.name ?? ''}
                img={{
                    img: device?.image ?? '',
                    alt: device?.name ?? ''
                }}
                subContent={
                    device ? (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8
                            }}
                        >
                            <DeviceIdLabel deviceId={device.id} />
                            <DeviceStatus
                                device={device}
                                showBattery={true}
                                showLocation={true}
                            />
                        </div>
                    ) : undefined
                }
            />

            {status === 'pending' && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <UISpinner />
                </div>
            )}

            {status === 'error' && (
                <div>
                    <UIInlineAlert text={error} />
                    {canRetry && (
                        <>
                            <UISpacer />
                            <UIButton onClick={refetch} variant="primary">
                                <UIText>{t('shared.labels.retry')}</UIText>
                            </UIButton>
                        </>
                    )}
                </div>
            )}

            {device && (
                <>
                    <DeviceGeneralSection device={device} canEdit={canEdit} />
                    <DeviceAlertsSection device={device} canEdit={canEdit} />
                    <DeviceGeofenceSection device={device} canEdit={canEdit} />
                    <DeviceWifiSection device={device} canEdit={canEdit} />
                    <DeviceActionsSection device={device} canEdit={canEdit} />
                </>
            )}
        </div>
    );
}
